/**
 * Risograph astronomy, turned up. Flat misregistered fills, halftone grain and
 * thick linework are still the house style — but the palette is bright and
 * saturated rather than muted, and no region's ground is near-black. A place
 * you travel to should announce itself with colour before you read its name.
 *
 * A palette swaps wholesale as you travel, margin included, so each region
 * reads as a different print run.
 */
export interface SystemPalette {
  id: string;
  name: string;
  /** The playfield ground. Deep and saturated, never black. */
  void: number;
  /** Warm off-white, used for linework, UI text and the desktop margin. */
  paper: number;
  /** The one hot accent. Reserved for player output — never on an enemy. */
  hot: number;
  /**
   * Orbital elements — rings and empty mounts. A cool cyan family in every
   * region: it reads as equipment and space rather than as a warning, and it
   * stays clear of the hot accent, which means "you did that".
   */
  ring: number;
  /** Enemy family base. */
  threat: number;
  /** Juice colour. Always reads as money, whatever the region. */
  juice: number;
  /** Grain overlay tint. */
  grain: number;
}

export const SYSTEM_PALETTES: Record<string, SystemPalette> = {
  'inner-reach': {
    id: 'inner-reach',
    name: 'The Inner Reach',
    void: 0x16394f,
    paper: 0xfff4e0,
    hot: 0xff7a3d,
    ring: 0x5fd3e8,
    threat: 0xff4f6b,
    juice: 0xffd93d,
    grain: 0x00131f,
  },
  'forge-belt': {
    id: 'forge-belt',
    name: 'The Forge Belt',
    void: 0x4a1233,
    paper: 0xfff0e2,
    hot: 0xffd166,
    ring: 0x63c8e0,
    threat: 0xff3b5c,
    juice: 0xffe14d,
    grain: 0x20000e,
  },
  'outer-rim': {
    id: 'outer-rim',
    name: 'The Outer Rim',
    void: 0x123a5c,
    paper: 0xeaf7ff,
    hot: 0xff8a4d,
    ring: 0x7fe3f0,
    threat: 0xc25bff,
    juice: 0xffd93d,
    grain: 0x001428,
  },
  scrapfields: {
    id: 'scrapfields',
    name: 'The Scrapfields',
    void: 0x2b1b4d,
    paper: 0xfdf3df,
    hot: 0xffd166,
    ring: 0x5ad9e6,
    threat: 0xff5e8a,
    juice: 0xffe14d,
    grain: 0x0d0020,
  },
  'deep-well': {
    id: 'deep-well',
    name: 'The Deep Well',
    void: 0x35124d,
    paper: 0xffeaf7,
    hot: 0xff8ad0,
    ring: 0x8ed0f5,
    threat: 0xff7ad0,
    juice: 0xffd93d,
    grain: 0x18002a,
  },
  beyond: {
    id: 'beyond',
    name: 'Beyond',
    void: 0x14202e,
    paper: 0xe9eef5,
    hot: 0xff6b35,
    ring: 0x74c6e0,
    threat: 0xff2d55,
    juice: 0xffc93d,
    grain: 0x000810,
  },
};

export function paletteFor(region: string): SystemPalette {
  return SYSTEM_PALETTES[region] ?? SYSTEM_PALETTES['inner-reach']!;
}
